import { useState } from "react";

import { FallbackRender } from "@sentry/react";
import { useCopyToClipboard } from "@uidotdev/usehooks";
import { LuCircleCheck, LuCopy } from "react-icons/lu";

import { Button } from "@shared/components/ui/Button";
import { cn } from "@shared/utils/cn";
import { config } from "@shared/config";
import { useCurrentVersion } from "@shared/features/app/hooks/use-current-version";
import { SupportDrawer } from "@shared/features/support/components/support-drawer";

import {
  Modal,
  ModalClose,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalTitle,
  ModalTrigger,
} from "./ui/modal";

export const ErrorView: FallbackRender = ({ error, resetError }) => {
  const onReset = resetError;
  const copyToClipboard = useCopyToClipboard()[1];
  const version = useCurrentVersion();

  const [isCopied, setIsCopied] = useState(false);
  const [isErrorShown, setIsErrorShown] = useState<boolean>(
    ["LOCAL", "DEV"].includes(config.mode)
  );

  const handleCopy = (e) => {
    e.preventDefault();
    copyToClipboard(
      JSON.stringify(error, Object.getOwnPropertyNames(error), 2)
    );
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 5000);
  };

  return (
    <div
      className={cn(
        "absolute w-full h-full flex flex-col items-center justify-center"
      )}
    >
      <div className="text-center">
        <h1 className="text-3xl font-medium text-gray-900">
          Une erreur est survenue
        </h1>
        <p className="mt-2 text-sm text-gray-500">
          Nous sommes navrés pour la gêne occasionnée. Nos équipes ont été
          prévenu.
          <br />
        </p>
        <Modal
          open={isErrorShown}
          onOpenChange={(open) => setIsErrorShown(open)}
        >
          <ModalTrigger>
            <Button
              variant="ghost"
              className="absolute bottom-5 left-1/2 -translate-x-1/2"
            >
              Afficher les détails de l&apos;erreur
            </Button>
          </ModalTrigger>
          <ModalContent className="px-12 pb-9 pt-11 max-w-screen-2xl">
            <ModalClose></ModalClose>
            <ModalTitle>
              {error instanceof Error ? error.name : "Error"}:{" "}
              {error instanceof Error ? error.message : String(error)}
            </ModalTitle>
            <ModalDescription className=" text-gray800/80 mb-3">
              <pre>
                <code className="whitespace-pre-line">
                  {error instanceof Error ? error.stack : String(error)}
                </code>
              </pre>
            </ModalDescription>
            <ModalFooter className="w-full ">
              <Button
                variant="outline"
                onClick={handleCopy}
                disabled={isCopied}
              >
                {isCopied ? (
                  <>
                    <LuCircleCheck className="w-5 h-5 mr-2" />
                    <span>Copié</span>
                  </>
                ) : (
                  <>
                    <LuCopy className="w-5 h-5 mr-2" />
                    <span>Copier</span>
                  </>
                )}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </div>
      <div className="mt-4">
        <Button onClick={onReset}>Réessayer</Button>
      </div>
      {version && <p className="opacity-50 text-xs mt-4">v{version}</p>}
      <SupportDrawer />
    </div>
  );
};
